import type { MotionEase } from "./types";
export const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
function cubicBezier(t: number, values: readonly number[]): number {
  const [x1, y1, x2, y2] = values;
  const curve = (u: number, a: number, b: number) =>
    3 * (1 - u) ** 2 * u * a + 3 * (1 - u) * u * u * b + u ** 3;
  let low = 0;
  let high = 1;
  for (let i = 0; i < 32; i++) {
    const middle = (low + high) / 2;
    if (curve(middle, x1, x2) < t) low = middle;
    else high = middle;
  }
  return curve((low + high) / 2, y1, y2);
}
function bounce(t: number): number {
  const n = 7.5625;
  const d = 2.75;
  if (t < 1 / d) return n * t * t;
  if (t < 2 / d) return n * (t -= 1.5 / d) * t + 0.75;
  if (t < 2.5 / d) return n * (t -= 2.25 / d) * t + 0.9375;
  return n * (t -= 2.625 / d) * t + 0.984375;
}
export function evaluateEasing(
  ease: MotionEase = "linear",
  progress: number,
): number {
  const t = clamp01(Number.isFinite(progress) ? progress : 0);
  if (t === 0 || t === 1) return t;
  if (typeof ease === "object") {
    if (ease.type === "cubic-bezier") return cubicBezier(t, ease.values);
    if (ease.type === "steps") return Math.floor(t * ease.count) / ease.count;
    const stiffness = ease.stiffness ?? 100;
    const damping = ease.damping ?? 10;
    const mass = ease.mass ?? 1;
    const w0 = Math.sqrt(stiffness / mass);
    const zeta = damping / (2 * Math.sqrt(stiffness * mass));
    if (zeta < 1) {
      const wd = w0 * Math.sqrt(1 - zeta * zeta);
      return (
        1 -
        Math.exp(-zeta * w0 * t) *
          (Math.cos(wd * t) + ((zeta * w0) / wd) * Math.sin(wd * t))
      );
    }
    if (Math.abs(zeta - 1) < 1e-6) return 1 - Math.exp(-w0 * t) * (1 + w0 * t);
    const root = Math.sqrt(zeta * zeta - 1);
    const r1 = -w0 * (zeta - root);
    const r2 = -w0 * (zeta + root);
    return 1 - (r2 * Math.exp(r1 * t) - r1 * Math.exp(r2 * t)) / (r2 - r1);
  }
  if (ease === "ease-in") return cubicBezier(t, [0.42, 0, 1, 1]);
  if (ease === "ease-out") return cubicBezier(t, [0, 0, 0.58, 1]);
  if (ease === "ease-in-out") return cubicBezier(t, [0.42, 0, 0.58, 1]);
  if (ease === "bounce") return bounce(t);
  if (ease === "elastic")
    return 2 ** (-10 * t) * Math.sin((t * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1;
  if (ease === "back") {
    const c = 1.70158;
    return 1 + (c + 1) * (t - 1) ** 3 + c * (t - 1) ** 2;
  }
  const steps = /^steps\((\d+)\)$/.exec(ease);
  if (steps) return Math.floor(t * Number(steps[1])) / Number(steps[1]);
  return t;
}
